import { Component, EventEmitter, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { MatStepper } from '@angular/material/stepper';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store } from '@ngxs/store';
import { SortAllUsers } from 'app/library/all-users/actions';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { CertificateRecordService } from 'app/library/certificate-record/services/certificate-record.service';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { CertificateTaxonomyFieldsComponent } from 'app/library/certifications/components/certificate-taxonomy-fields/certificate-taxonomy-fields.component';
import { Certification } from 'app/library/certifications/models/certification';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-certificate-dialog',
    templateUrl: './add-certificate-dialog.component.html',
    styleUrls: ['./add-certificate-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AddCertificateDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    private static certificateFormCache: {
        providerId: string;
        certificationId: string;
        issueDate: Date | null;
        expirationDate: Date | null;
        notifyAboutExpiration: boolean;
        createSilently: boolean;
    } | null = null;

    @Input()
    entitie$: Observable<User[]>;

    @Input()
    title: string;

    @Input()
    totalCount$: Observable<number>;

    @Input()
    includeSubteams: boolean;

    @Input()
    dataType: string;

    @Input()
    teamId: string;

    get shouldShowNextButton(): boolean {
        return this.stepper?.selectedIndex === 0;
    }

    get isLastStep(): boolean {
        return this.stepper?.steps && this?.stepper.selectedIndex === this?.stepper.steps.length - 1;
    }

    @ViewChild(MatStepper) stepper: MatStepper;
    @ViewChild(CertificateTaxonomyFieldsComponent) taxonomyFields: CertificateTaxonomyFieldsComponent;

    onConfirm = new EventEmitter<User>();

    isToDoFeatureEnabled: boolean = false;
    dataSource: AllUserDataSource;
    displayedColumns = [];
    searchInput = new FormControl();
    selectedCertificationProviderId: string;
    selectedCertificationId: string;
    selectedCertification: Certification;
    certificationForm: FormGroup;
    uploadedCertificateFiles: File[] = [];
    isSaveButtonLoading: boolean = false;
    selectedUser: User;
    minDate: Date;
    Validators = Validators;
    hasEndDate = new FormControl(false);
    isDragOver = false;
    createSilently = new FormControl(false);
    private _hasAppliedCachedCertificateFormValues = false;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    constructor(
        private _store: Store,
        public dialogRef: MatDialogRef<AddCertificateDialogComponent>,
        private _action$: Actions,
        private _featureService: FeatureService,
        private _translateService: TranslateService,
        private _certificateRecordService: CertificateRecordService,
        private _fb: FormBuilder
    ) {
        super();
    }

    ngOnInit(): void {
        this.isToDoFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.ToDo);

        this.certificationForm = this._fb.group({
            provider: [null, Validators.required],
            certification: [{ value: null, disabled: true }, Validators.required],
            issueDate: [null, Validators.required],
            document: [null],
        });

        this.updateExpirationDate();

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            this.loadUsersPage(true);
        });

        this.dataSource = new AllUserDataSource(this.entitie$, this._store, this._action$);

        setTimeout(() => {
            this.sort?.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortAllUsers(sort));

                this.loadUsersPage(true);
            });

            this.paginator?.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });

            void this.applyCachedCertificateFormValues();
        });

        this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
    }

    updateExpirationDate(): void {
        const startDate = this.certificationForm.get('issueDate').value;

        this.minDate = startDate ? new Date(startDate) : new Date();
    }

    goForward(stepper: MatStepper): void {
        stepper.next();
    }

    onRemoveAddedCertificate() {
        this.uploadedCertificateFiles = [];
    }

    async onStepChange(stepper: MatStepper): Promise<void> {
        const currentIndex = stepper.selectedIndex;

        if (currentIndex === 0) {
            this.title = this._translateService.instant('PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.CHOOSE_PARTICIPANTS');
        } else if (currentIndex === 1) {
            this.title = this._translateService.instant('PROGRAM_OFFERING.BUTTON.BOOK_INTO_EXECUTIONS');
            await this.applyCachedCertificateFormValues();
        }
    }

    removeFromList(user: User): void {
        this.selectedUser = null;

        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id) {
                            item = Object.assign(new ModuleUser(), item, {
                                selected: false,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    addToList(user: User): void {
        this.selectedUser = user;

        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === this.selectedUser.id) {
                            item = Object.assign(new ModuleUser(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    confirm(user: User): void {
        this.onConfirm.next(user);
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    onFileDropped(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;

        if (event.dataTransfer && event.dataTransfer.files.length > 0) {
            const files: File[] = Array.from(event.dataTransfer.files);
            this.handleFiles(files);
        }
    }

    handleFiles(files: File[]): void {
        const fileSubject = new BehaviorSubject<File | null>(null);
        const collectedFiles: File[] = [];

        fileSubject
            .asObservable()
            .pipe(
                filter((file) => !!file),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((file) => {
                collectedFiles.push(file!);
                if (collectedFiles.length === files.length) {
                    fileSubject.complete();
                    this.fileChange(collectedFiles);
                }
            });

        files.forEach((file) => fileSubject.next(file));
    }

    fileChange(files: FileList | File[]): void {
        if (files instanceof FileList) {
            const temp: File[] = [];
            for (const key of Object.keys(files)) {
                temp[key] = files[key];
            }
            files = temp;
        }

        this.uploadedCertificateFiles = files;
    }

    onProviderSelected(provider: CertificationProvider): void {
        this.selectedCertificationProviderId = provider.id;
        this.selectedCertificationId = null;
        this.selectedCertification = null;
        this.hasEndDate.setValue(false);
        this.certificationForm.removeControl('expirationDate');
        if (this.isToDoFeatureEnabled) {
            this.certificationForm.removeControl('notifyAboutExpiration');
        }
    }

    onCertificationSelected(certification: Certification): void {
        this.selectedCertificationId = certification.id;
        this.selectedCertification = certification;

        if (this.selectedCertification.certificationName === 'other') {
            this.hasEndDate.setValue(true);
        } else {
            this.hasEndDate.setValue(false);
        }

        if (this.selectedCertification.hasExpirationDate || this.selectedCertification.certificationName === 'other') {
            if (!this.certificationForm.get('expirationDate')) {
                this.certificationForm.addControl('expirationDate', new FormControl(null, Validators.required));
            }
            if (this.isToDoFeatureEnabled && !this.certificationForm.get('notifyAboutExpiration')) {
                this.certificationForm.addControl('notifyAboutExpiration', new FormControl(false));
            }
        } else {
            this.certificationForm.removeControl('expirationDate');
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.removeControl('notifyAboutExpiration');
            }
        }
    }

    onChangeEndDateToggle(event: MatSlideToggleChange): void {
        if (event.checked) {
            this.certificationForm.addControl('expirationDate', new FormControl(null, Validators.required));
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.addControl('notifyAboutExpiration', new FormControl(false));
            }
        } else {
            this.certificationForm.removeControl('expirationDate');
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.removeControl('notifyAboutExpiration');
            }
        }
    }

    async saveSelected(): Promise<void> {
        this.isSaveButtonLoading = true;

        const certificateRecord = await this._certificateRecordService.addCertificateRecord(
            this.selectedCertificationId,
            this.certificationForm.get('issueDate').value,
            this.certificationForm.get('expirationDate')?.value,
            this.selectedUser.id,
            this.uploadedCertificateFiles[0],
            this.certificationForm.get('notifyAboutExpiration')?.value || false,
            !this.createSilently.value || false
        );

        this.isSaveButtonLoading = false;

        if (certificateRecord) {
            this.cacheCurrentCertificateFormValues();
            this.dialogRef.close(true);
        }
    }

    private async applyCachedCertificateFormValues(): Promise<void> {
        const cachedValues = AddCertificateDialogComponent.certificateFormCache;
        if (!cachedValues || !this.taxonomyFields || this._hasAppliedCachedCertificateFormValues) {
            return;
        }

        this._hasAppliedCachedCertificateFormValues = true;

        await this.taxonomyFields.selectProviderById(cachedValues.providerId);
        if (!this.selectedCertificationProviderId) {
            AddCertificateDialogComponent.certificateFormCache = null;
            return;
        }

        await this.taxonomyFields.selectCertificationById(cachedValues.certificationId);
        if (!this.selectedCertificationId) {
            return;
        }

        this.certificationForm.get('issueDate').setValue(cachedValues.issueDate);
        this.updateExpirationDate();

        if (this.certificationForm.get('expirationDate')) {
            this.certificationForm.get('expirationDate').setValue(cachedValues.expirationDate);
        }

        if (this.certificationForm.get('notifyAboutExpiration')) {
            this.certificationForm.get('notifyAboutExpiration').setValue(cachedValues.notifyAboutExpiration);
        }

        this.createSilently.setValue(cachedValues.createSilently);
    }

    private cacheCurrentCertificateFormValues(): void {
        AddCertificateDialogComponent.certificateFormCache = {
            providerId: this.selectedCertificationProviderId,
            certificationId: this.selectedCertificationId,
            issueDate: this.certificationForm.get('issueDate').value || null,
            expirationDate: this.certificationForm.get('expirationDate')?.value || null,
            notifyAboutExpiration: this.certificationForm.get('notifyAboutExpiration')?.value || false,
            createSilently: this.createSilently.value || false,
        };
    }

    async loadUsersPage(resetPageIndex = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.dataSource) {
            this.dataSource.loadUsers(
                this.dataType,
                [],
                this.teamId || '',
                this.searchInput.value,
                this.sort.active,
                this.sort.direction,
                this.paginator.pageSize,
                this.paginator.pageIndex + 1,
                null,
                '',
                null,
                '',
                [],
                [],
                [],
                [],
                null,
                null,
                null,
                [],
                [],
                this.includeSubteams,
                this.dataType === 'users' ? ['active'] : []
            );
        }
    }
}
